import type { RosterAgent, SidebarSection, SidebarSettings } from "@ark-bot/protocol";

import { orderRoster } from "./roster.js";

/**
 * How the roster is arranged, as the prototype arranges its bots: a pinned
 * rail on top, then sections, then whoever is in none of them, with hidden
 * bots out of sight until asked for. The arrangement is host settings, so it
 * is the same wherever the app is opened; these are the pure moves on it.
 */

export const EMPTY_SIDEBAR: SidebarSettings = { pinned: [], hidden: [], sections: [] };

export interface ArrangedSection {
  readonly id: string;
  readonly name: string;
  readonly agents: RosterAgent[];
}

export interface Arrangement {
  /** In the pinned order. */
  readonly pinned: RosterAgent[];
  /** Each section's rows, most recently moved first, in the sections' order. */
  readonly sections: ArrangedSection[];
  /** Neither pinned, sectioned nor hidden. */
  readonly unassigned: RosterAgent[];
  readonly hidden: RosterAgent[];
}

/** Places every agent once; an id the settings name but the roster does not have is simply not drawn. */
export function arrange(agents: readonly RosterAgent[], sidebar: SidebarSettings): Arrangement {
  const byId = new Map(agents.map((agent) => [agent.id, agent]));
  const taken = new Set<string>();
  const take = (id: string): RosterAgent | null => {
    const agent = byId.get(id);
    if (agent == null || taken.has(id)) return null;
    taken.add(id);
    return agent;
  };
  const hidden = sidebar.hidden.flatMap((id) => take(id) ?? []);
  const pinned = sidebar.pinned.flatMap((id) => take(id) ?? []);
  const sections = sidebar.sections.map((section) => ({ id: section.id, name: section.name, agents: orderRoster(section.agents.flatMap((id) => take(id) ?? [])) }));
  const unassigned = orderRoster(agents.filter((agent) => !taken.has(agent.id)));
  return { pinned, sections, unassigned, hidden };
}

function without(list: readonly string[], id: string): string[] {
  return list.filter((one) => one !== id);
}

function withoutInSections(sections: readonly SidebarSection[], id: string): SidebarSection[] {
  return sections.map((section) => ({ ...section, agents: without(section.agents, id) }));
}

/** Pins at the end of the rail, taking the agent out of its section; already pinned, it comes off the rail. */
export function togglePinned(sidebar: SidebarSettings, id: string): SidebarSettings {
  if (sidebar.pinned.includes(id)) return { ...sidebar, pinned: without(sidebar.pinned, id) };
  return { ...sidebar, pinned: [...sidebar.pinned, id], hidden: without(sidebar.hidden, id), sections: withoutInSections(sidebar.sections, id) };
}

/** Moves a pinned agent to sit before or after another on the rail; pins it first if it was not. */
export function reorderPinned(sidebar: SidebarSettings, id: string, targetId: string, position: "before" | "after"): SidebarSettings {
  if (id === targetId) return sidebar;
  const wasPinned = sidebar.pinned.includes(id);
  const rail = without(sidebar.pinned, id);
  const index = rail.indexOf(targetId);
  if (index < 0) return sidebar;
  const at = position === "before" ? index : index + 1;
  const pinned = [...rail.slice(0, at), id, ...rail.slice(at)];
  return wasPinned ? { ...sidebar, pinned } : { ...sidebar, pinned, hidden: without(sidebar.hidden, id), sections: withoutInSections(sidebar.sections, id) };
}

/** Puts the agent in one section (or in none, with null), unpinning and unhiding it. */
export function moveToSection(sidebar: SidebarSettings, id: string, sectionId: string | null): SidebarSettings {
  const sections = withoutInSections(sidebar.sections, id).map((section) => (section.id === sectionId ? { ...section, agents: [...section.agents, id] } : section));
  return { ...sidebar, pinned: without(sidebar.pinned, id), hidden: without(sidebar.hidden, id), sections };
}

/** A new, named section, optionally with one agent in it; the id is the caller's (a uuid, or a timestamp). */
export function addSection(sidebar: SidebarSettings, id: string, name: string, agentId?: string): SidebarSettings {
  const trimmed = name.trim();
  if (trimmed.length === 0) return sidebar;
  const withSection = { ...sidebar, sections: [...sidebar.sections, { id, name: trimmed, agents: [] }] };
  return agentId == null ? withSection : moveToSection(withSection, agentId, id);
}

export function renameSection(sidebar: SidebarSettings, id: string, name: string): SidebarSettings {
  const trimmed = name.trim();
  if (trimmed.length === 0) return sidebar;
  return { ...sidebar, sections: sidebar.sections.map((section) => (section.id === id ? { ...section, name: trimmed } : section)) };
}

/** Removes the section; its agents become unassigned. */
export function removeSection(sidebar: SidebarSettings, id: string): SidebarSettings {
  return { ...sidebar, sections: sidebar.sections.filter((section) => section.id !== id) };
}

/** Hides the agent from the list (it keeps its section for when it comes back), or brings it back. */
export function toggleHidden(sidebar: SidebarSettings, id: string): SidebarSettings {
  if (sidebar.hidden.includes(id)) return { ...sidebar, hidden: without(sidebar.hidden, id) };
  return { ...sidebar, hidden: [...sidebar.hidden, id], pinned: without(sidebar.pinned, id) };
}

/** The section an agent is in, if any. */
export function sectionOf(sidebar: SidebarSettings, id: string): SidebarSection | null {
  return sidebar.sections.find((section) => section.agents.includes(id)) ?? null;
}

/** Moves a section to sit before or after another; the unassigned block is not a section and stays last. */
export function reorderSection(sidebar: SidebarSettings, id: string, targetId: string, position: "before" | "after"): SidebarSettings {
  if (id === targetId) return sidebar;
  const moving = sidebar.sections.find((section) => section.id === id);
  if (moving == null) return sidebar;
  const rest = sidebar.sections.filter((section) => section.id !== id);
  const index = rest.findIndex((section) => section.id === targetId);
  if (index < 0) return sidebar;
  const at = position === "before" ? index : index + 1;
  return { ...sidebar, sections: [...rest.slice(0, at), moving, ...rest.slice(at)] };
}
